import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, FormGroup, Label, Input, Card, CardHeader, CardBody, Collapse } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loading from '../misc/Loading'
import { useTranslation } from 'react-i18next';
import StatusCode from '../../util/StatusCode';
import { getInstanceSettings, resetUpdateInstanceSettings, updateInstanceSettings } from '../../store/InstanceConfigAction';
import { RootState } from '../../store/store';
import { CoreInstanceSettings } from '../../models/CoreInstanceSettings';
import { EmptyRequestState } from '../../models/RequestState';
import { isEqual } from 'lodash'
import IntInput from '../misc/IntInput';
import { FlowSyncAccessPolicy, isAuthorized } from '../../models/FlowSyncAccessPolicy';

const InstanceConfig = () => {
    const dispatch = useDispatch();

    const instanceSettings = useSelector<RootState, CoreInstanceSettings | undefined>(state => state.instanceSettings.settings);
    const updateStatus = useSelector<RootState, EmptyRequestState>(state => state.instanceSettings.updateStatus);
    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);

    const [updatedSettings, setUpdatedConfiguration] = useState<CoreInstanceSettings | undefined>(undefined);

    const [isOpen, setOpen] = useState({
        mergePersons: true,
        dataPurging: true
    });

    const { t } = useTranslation(['configuration', 'common']);

    const hasEditAccess = isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_MODULE);

    useEffect(() => {
        getInstanceSettings()(dispatch);
        return () => {
            resetUpdateInstanceSettings()(dispatch);            
        }    
    }, [dispatch])

    const updatePart = (part: Partial<CoreInstanceSettings>) => {
        if (instanceSettings === undefined) {
            return;
        }
        else if (updatedSettings === undefined) {
            setUpdatedConfiguration({ ...instanceSettings, ...part });
        }
        else {
            setUpdatedConfiguration({ ...updatedSettings, ...part });
        }
    }

    const saveUpdates = async () => {
        if (!hasUpdates || updatedSettings === undefined) {
            return;
        }

        const res = await updateInstanceSettings(updatedSettings)(dispatch);
        if (res?.status === 200) {
            getInstanceSettings()(dispatch);
            setUpdatedConfiguration(undefined);        
        }
    }

    const renderSaveStatus = () => {
        switch (updateStatus.code) {
            case StatusCode.PENDING: return <Loading />;
            case StatusCode.ERROR: return <>
                <FontAwesomeIcon icon="exclamation-triangle" style={{ fontSize: '22px', verticalAlign: 'middle', color: '#ffc107' }} />
                <span style={{ marginLeft: '0.5rem' }}>{t('error.somethingWentWrong', { ns: 'common' })}</span>
            </>;
            case StatusCode.COMPLETE: return <>
                <FontAwesomeIcon icon="check" style={{ fontSize: '22px', verticalAlign: 'middle', color: '#27807d' }} />
                <span style={{ marginLeft: '0.5rem' }}>{t('moduleView.saved')}</span>
            </>;
            default: return null;
        }
    }

    const hasUpdates = useMemo(() => {
        if (!instanceSettings || !updatedSettings) {
            return false;
        }

        return !isEqual(instanceSettings, updatedSettings);
    }, [instanceSettings, updatedSettings]);

    const settings = updatedSettings ?? instanceSettings;

    const validationErrors = useMemo(() => {
        const errors: string[] = [];
        if (!settings) {
            return errors;
        }

        if (settings.purgeAfterDays < 30 || settings.purgeAfterDays > 365) {
            errors.push('purgeAfterDaysRange');
        }

        return errors;
    }, [settings]);

    const renderInstanceConfig = (settings: CoreInstanceSettings) => {
        const disableSave = !hasUpdates || !hasEditAccess || validationErrors.length > 0;

        return <>
            <h1>{t('instanceSettings.title', { ns: 'configuration' })}</h1>

            <p>{t('instanceSettings.infoText', { ns: 'configuration' })}</p>

            <Card>
                <CardHeader tag="h5">
                    <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setOpen({ ...isOpen, mergePersons: !isOpen.mergePersons })}>{isOpen.mergePersons ? '-' : '+'}</Button>
                    {t('instanceSettings.mergePeopleSection', { ns: 'configuration' })}
                </CardHeader>
                <CardBody>
                    <Collapse isOpen={isOpen.mergePersons}>
                        <FormGroup check name="mergePersonsAtImport">
                            <Label check style={{ display: 'block', marginBottom: '8px' }}>
                                <Input
                                    type="checkbox"
                                    name="mergePersonsAtImport"
                                    id="mergePersonsAtImport"
                                    disabled={!hasEditAccess}
                                    checked={settings.mergePersonsAtImport}
                                    onChange={e => updatePart({ mergePersonsAtImport: e.target.checked })}
                                /> {t('instanceSettings.mergePeople', { ns: 'configuration' })}
                            </Label>
                        </FormGroup>
                    </Collapse>
                </CardBody>
            </Card>

            <Card className="mt-3">
                <CardHeader tag="h5">
                    <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setOpen({ ...isOpen, dataPurging: !isOpen.dataPurging })}>{isOpen.dataPurging ? '-' : '+'}</Button>
                    {t('instanceSettings.purgeDataSection', { ns: 'configuration' })}
                </CardHeader>
                <CardBody>
                    <Collapse isOpen={isOpen.dataPurging}>

                        <FormGroup check name="purgeData">
                            <Label check style={{ display: 'block', marginBottom: '8px' }}>
                                <IntInput
                                    name="purgeAfterDays"
                                    title={t('instanceSettings.purgeAfterDays', { ns: 'configuration' })}
                                    updateValue={(_, v: number) => updatePart({ purgeAfterDays: v })}
                                    value={settings.purgeAfterDays}
                                    disabled={!hasEditAccess}
                                    min={30}
                                    max={365}
                                    invalid={validationErrors.indexOf('purgeAfterDaysRange') !== -1}
                                    invalidFeedback={t('instanceSettings.purgeAfterDaysInvalidRange', { ns: 'configuration' })}
                                />
                            </Label>
                        </FormGroup>
                    </Collapse>
                </CardBody>
            </Card>

            <div className="edit_module">
                    
                <Button color="primary" disabled={disableSave} onClick={saveUpdates}>
                    {t('action.save', { ns: 'common' })}
                </Button>
                <div style={{ marginLeft: '1rem', display: 'inline-block' }}>
                    {renderSaveStatus()}
                </div>

            </div>                           
        </>;
    }

    return <div className="padding-40 container single">
        {settings === undefined && <Loading />}
        {settings !== undefined && renderInstanceConfig(settings)}
    </div>;
}

export default InstanceConfig;